import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Tabs,
  Card,
  Group,
  Stack,
  Text,
  Button,
  Avatar,
  ActionIcon,
  Menu,
  Badge,
  LoadingOverlay,
  Paper,
  Divider,
  Tooltip,
  Box,
  Modal,
  SimpleGrid,
  ThemeIcon
} from '@mantine/core';
import { useGroup } from '../../hooks/useGroup';
import { useGroupCategories } from '../../hooks/useGroupCategories';
import { ExpenseList } from './ExpenseList';
import { GroupDiscussion } from './GroupDiscussion';
import { MemberList } from './MemberList';
import GroupActivityFeed from './GroupActivityFeed';
import { AddExpenseModal } from './expense/AddExpenseModal';
import { SettleUpModal } from './SettleUpModal';
import { InviteMemberModal } from './InviteMemberModal';
import settingsIcon from '../../assets/icons/settings.svg';
import shareIcon from '../../assets/icons/share.svg';
import inviteIcon from '../../assets/icons/users.svg';
import { useAuthInfo } from '@propelauth/react';
import type { Member } from '../../hooks/useGroupMembers';
import { GroupMember } from '../../types/group';
import { formatCurrency } from '../../utils/formatters';
import { env } from '../../utils/env';
import { showSuccessNotification, showErrorNotification } from '../../utils/notificationUtils';

// Debug utility
const DEBUG = env.DEBUG_MODE;
const debugLog = (...args: any[]) => {
  if (DEBUG) {
    console.log(...args);
  }
};

interface GroupDetailViewProps {
  groupId: string;
  accessToken: string;
  onRefresh?: () => void; // Add callback for parent refresh
  isVisible?: boolean; // Add isVisible prop
}

export const GroupDetailView = ({ groupId, accessToken, onRefresh, isVisible = true }: GroupDetailViewProps) => {
  const { user } = useAuthInfo();
  const { group, loading, error, refresh, leaveGroup } = useGroup(groupId, accessToken, user?.userId, { isVisible });
  const { categories, loading: categoriesLoading, refresh: refreshCategories } = useGroupCategories(accessToken, groupId);
  const [activeTab, setActiveTab] = useState<string | null>('expenses');
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showSettleUp, setShowSettleUp] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [hasActivities, setHasActivities] = useState(false);
  const [activityTabVisited, setActivityTabVisited] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [expenseRefreshTrigger, setExpenseRefreshTrigger] = useState<number>(0);
  const isRefreshingRef = useRef(false); // Prevent multiple rapid refreshes
  const [localUnreadCount, setLocalUnreadCount] = useState(0);
  const [editingExpense, setEditingExpense] = useState<any>(null); // Add state for editing expense

  // Update the ref type to be compatible with what GroupActivityFeed expects
  const activityFeedRef = useRef<{ markAsSeen: () => Promise<any> }>(null);

  // Add leave group modal state
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [leaveGroupData, setLeaveGroupData] = useState<{
    hasOutstandingBalances: boolean;
    formattedBalanceTotal: string;
  }>({ hasOutstandingBalances: false, formattedBalanceTotal: "0" });

  // Replace the useMemo hooks with a function that only runs when needed
  const calculateUserBalances = useCallback(() => {
    // Early return with default values if no data
    if (!user?.userId || !group?.balances?.length) {
      return { hasOutstandingBalances: false, formattedBalanceTotal: "0" };
    }

    const members = group?.members || [];
    const balances = group?.balances || [];

    // Find the current user's member ID once
    const currentMember = members.find(m => m.user_id === user.userId);
    if (!currentMember) {
      return { hasOutstandingBalances: false, formattedBalanceTotal: "0" };
    }

    // Calculate totals in a single pass through balances
    let totalOwed = 0;
    let currency = "USD";
    let hasBalance = false;

    // Only loop through balances once
    for (const b of balances) {
      if (b.from_member_id === currentMember.id) {
        const amount = parseFloat(String(b.amount));
        if (amount > 0) {
          hasBalance = true;
          totalOwed += amount;
          currency = b.currency_code;
        }
      }
    }

    const formattedBalanceTotal = formatCurrency(totalOwed, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    return {
      hasOutstandingBalances: hasBalance,
      formattedBalanceTotal
    };
  }, [user?.userId, group?.members, group?.balances]);

  // Derived values from the optimized calculation
  const currentUserHasOutstandingBalances = leaveGroupData.hasOutstandingBalances;

  // Format the balance total only when needed
  const userBalanceTotal = leaveGroupData.formattedBalanceTotal;

  // Log when group data changes
  useEffect(() => {
    if (group) {
      debugLog('[GroupDetailView] Group data updated:', {
        id: group.id,
        name: group.name,
        members: group.members?.length || 0,
        balances: group.balances?.length || 0,
        activity: group.recent_activity?.length || 0
      });
    }
  }, [group]);

  // Handle refresh after expense changes (used by child components)
  const handleExpenseChange = useCallback(() => {
    debugLog('[GroupDetailView] Expense changed, refreshing data');
    refresh();

    // Call the parent refresh callback if it exists
    if (onRefresh) {
      debugLog('[GroupDetailView] Calling parent onRefresh from handleExpenseChange');
      onRefresh();
    }

    // Trigger an explicit refresh for ExpenseList
    setExpenseRefreshTrigger(prev => prev + 1);
  }, [refresh, onRefresh]);

  // Handle refresh after settlement changes (used by child components)
  const handleSettlementChange = useCallback(() => {
    debugLog('[GroupDetailView] Settlement changed, refreshing group data');
    // Just call refresh() to update this component's data
    refresh();

    // We're intentionally NOT calling onRefresh() here to prevent an infinite
    // refresh loop with parent components. GroupDetailView's refresh() will
    // update all the necessary data within this component's scope.
  }, [refresh]);

  // Log active tab changes
  useEffect(() => {
    debugLog('[GroupDetailView] Active tab changed to:', activeTab);
  }, [activeTab]);

  // Add this useEffect to initialize the local unread count from group data
  useEffect(() => {
    if (group?.unread_activity_count !== undefined) {
      setLocalUnreadCount(group.unread_activity_count);
    }
  }, [group?.unread_activity_count]);

  // Log when the activity state changes
  useEffect(() => {
    // Log when the activity state changes
    debugLog('[GroupDetailView] Activity state updated:', {
      hasActivities,
      activityTabVisited,
      unreadCount: localUnreadCount,
      groupUnreadCount: group?.unread_activity_count || 0
    });
  }, [hasActivities, activityTabVisited, localUnreadCount, group?.unread_activity_count]);

  // Add an effect to call markAsSeen when the component loads with activity tab selected
  useEffect(() => {
    // Call markAsSeen if activities are loaded and activity tab is already selected
    if (activeTab === 'activity' && !loading && group) {
      console.log('[GroupDetailView] Activity tab is initially active, setting unread count to 0');
      // When activity tab is active, immediately set unread count to 0
      setLocalUnreadCount(0);

      if (activityFeedRef.current?.markAsSeen) {
        console.log('[GroupDetailView] Activity tab is initially active, calling markAsSeen on load');
        activityFeedRef.current.markAsSeen().catch(err => {
          console.warn('[GroupDetailView] Error calling markAsSeen on initial load:', err);
        });
      }
    }
  }, [activeTab, loading, group]);

  // Add a useEffect to log the members and lastViewedAt data
  useEffect(() => {
    if (group && group.members && group.members.length > 0 && user?.userId) {
      const currentMember = group.members.find(m => (m as any).user_id === user?.userId);
      console.log('[GroupDetailView] Member data for current user:', {
        userId: user?.userId,
        member: currentMember,
        lastViewedAt: (currentMember as any)?.last_viewed_at || 'not found',
        allMembers: group.members.map(m => ({
          id: m.id,
          email: m.email,
          userId: (m as any).user_id,
          lastViewedAt: (m as any).last_viewed_at
        }))
      });
    }
  }, [group, user?.userId]);

  if (loading) {
    return (
      <Card p="xl" style={{ position: 'relative', minHeight: 200 }}>
        <LoadingOverlay visible={true} />
      </Card>
    );
  }

  if (error || !group) {
    return (
      <Card p="xl">
        <Text c="red">{error || 'Group not found'}</Text>
      </Card>
    );
  }

  // Safely access members and balances with fallbacks
  const members = group.members || [];
  const balances = group.balances || [];

  // Get ghost member counts for those with status 'ghost'
  const ghostMemberCount = members.filter(m => m.status === 'ghost').length;
  const tempGhostCount = members.filter(m =>
    m.status === 'ghost' && m.notification_preferences?.is_temporary_ghost === true
  ).length;
  const regularGhostCount = ghostMemberCount - tempGhostCount;

  // Determine ghost limit based on user tier (should be fetched from user info ideally)
  const getGhostLimit = () => {
    // This is an approximation; ideally we'd get the user's tier from the API
    if (regularGhostCount >= 20) return 20;  // Pro tier
    if (regularGhostCount >= 7) return 7;    // Basic tier
    return 3;                                // Free tier
  };

  // Modify the membersList mapping to handle last_viewed_at safely
  const membersList: GroupMember[] = members.map(m => ({
    id: m.id,
    email: m.email,
    display_name: m.display_name || '',
    role: m.role as 'owner' | 'admin' | 'member',
    status: m.status === 'removed' ? 'pending' : m.status as 'active' | 'pending' | 'ghost', // Handle 'removed' status by treating as 'pending'
    joined_at: m.joined_at,
    avatar_url: m.avatar_url,
    user_id: m.user_id, // Add the user_id field
    is_ghost: m.is_ghost, // Pass is_ghost from backend data
    last_viewed_at: m.last_viewed_at, // Add last_viewed_at if it's part of GroupMember
    // Ensure notification_preferences has correct type
    notification_preferences: m.notification_preferences ? {
      is_temporary_ghost: !!m.notification_preferences.is_temporary_ghost
    } : undefined
  }));

  // Group balances by member to show summary
  const renderBalanceSummary = () => {
    const currentBalances = group.balances || [];
    if (currentBalances.length === 0) {
      return (
        <Text c="dimmed" ta="center" py="sm">No outstanding balances found. This might be unexpected if there are expenses.</Text>
      );
    }

    return (
      <Stack gap="xs">
        {currentBalances.map((balance, index) => (
          <Group key={index} justify="space-between" p="xs">
            <Group>
              <Avatar
                radius="xl"
                size="sm"
                src={members.find(m => m.id === balance.from_member_id)?.avatar_url}
              >
                {(balance.from_member_name || '?').charAt(0).toUpperCase()}
              </Avatar>
              <Text size="sm">
                {balance.from_member_name} owes {balance.to_member_name}
              </Text>
            </Group>
            <Text fw={500} size="sm" c="green">
              {formatCurrency(balance.amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
          </Group>
        ))}
      </Stack>
    );
  };

  const refreshGroupData = () => {
    // Prevent multiple rapid refreshes
    if (isRefreshingRef.current) {
      console.log('[GroupDetailView] Already refreshing, skipping duplicate refresh request');
      return;
    }

    console.log('[GroupDetailView] Refreshing all group data');
    isRefreshingRef.current = true;

    // Call individual refresh functions
    handleExpenseChange();
    handleSettlementChange();

    // Update refresh trigger for activity feed
    setRefreshTrigger(prevTrigger => {
      const newTrigger = prevTrigger + 1;
      console.log(`[GroupDetailView] Setting new refresh trigger: ${newTrigger}`);
      return newTrigger;
    });

    // Also refresh the group data itself
    if (refresh) {
      console.log('[GroupDetailView] Calling group.refresh()');
      refresh();
    }

    // Reset the refresh flag after a delay
    setTimeout(() => {
      isRefreshingRef.current = false;
      console.log('[GroupDetailView] Reset refresh flag, can refresh again');
    }, 1000);
  };

  // Modify tab change handling to track activity tab visits
  const handleTabChange = (value: string | null) => {
    if (!value) return;

    debugLog(`[GroupDetailView] Tab changed from ${activeTab} to ${value}`);

    // Track when activity tab is visited
    if (value === 'activity') {
      debugLog('[GroupDetailView] Activity tab visited');

      // Reset unread count when activity tab is selected
      console.log('[GroupDetailView] Resetting unread activity count');
      setLocalUnreadCount(0);

      // Call markAsSeen to update the last_viewed_at timestamp
      if (activityFeedRef.current && activityFeedRef.current.markAsSeen) {
        console.log('[GroupDetailView] Explicitly calling markAsSeen on tab activation');
        activityFeedRef.current.markAsSeen().catch(err => {
          console.warn('[GroupDetailView] Error calling markAsSeen:', err);
        });
      }

      if (!activityTabVisited) {
        debugLog('[GroupDetailView] First visit to activity tab');
        setActivityTabVisited(true);
      }
    }

    setActiveTab(value);
  }

  return (
    <div className="group-detail-container w-full max-w-4xl mx-auto p-4">
      <Card withBorder>
        <Group justify="space-between">
          <Group>
            <Avatar
              src={null}
              color="blue"
              radius="xl"
              size="lg"
            >
              {(group.name || '?').charAt(0).toUpperCase()}
            </Avatar>

            <Stack gap={4}>
              <Text size="xl" fw={700}>{group.name || 'Unnamed Group'}</Text>
              {group.description && (
                <Text size="sm" c="dimmed">
                  {group.description}
                </Text>
              )}
            </Stack>
          </Group>

          <Group>
            <Button
              variant="light"
              leftSection={<img src={shareIcon} alt="" width={16} />}
              onClick={() => setShowSettleUp(true)}
            >
              Settle Up
            </Button>

            <Button
              onClick={() => setShowAddExpense(true)}
            >
              Add Expense
            </Button>

            <Menu position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle">
                  <img src={settingsIcon} alt="Settings" width={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<img src={inviteIcon} alt="" width={16} />}
                  onClick={() => setShowInvite(true)}
                >
                  Add Members
                </Menu.Item>
                <Menu.Item>Edit Group</Menu.Item>
                <Menu.Item color="red" onClick={() => {
                  // Only calculate balances when the leave modal is opened
                  setLeaveGroupData(calculateUserBalances());
                  setShowLeaveModal(true);
                }}>Leave Group</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </Card>

      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Tab value="expenses">
            Expenses
          </Tabs.Tab>
          <Tabs.Tab value="categories">
            Categories
            <Badge size="xs" ml={4}>
              {categories.length}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="activity">
            Activity
            {localUnreadCount > 0 && (
              <Badge size="xs" ml={4}>
                {localUnreadCount}
              </Badge>
            )}
          </Tabs.Tab>
          <Tabs.Tab value="discussion">
            Discussion
            {(group.unread_messages_count ?? 0) > 0 && (
              <Badge size="xs" ml={4}>
                {group.unread_messages_count}
              </Badge>
            )}
          </Tabs.Tab>
          <Tabs.Tab value="members">
            Members
          </Tabs.Tab>
        </Tabs.List>

        <Card mt="md" withBorder>
          <Tabs.Panel value="expenses">
            <Paper withBorder p="md" mb="lg">
              <Group justify="space-between" mb="xs">
                <Text fw={500}>
                  Outstanding Balances
                  <Tooltip label="These are simplified balances showing the minimum number of transactions needed to settle up">
                    <Text component="span" ml={5} style={{ cursor: 'help' }}>ⓘ</Text>
                  </Tooltip>
                </Text>
                <Button
                  variant="light"
                  size="xs"
                  onClick={() => setShowSettleUp(true)}
                >
                  Settle Up
                </Button>
              </Group>
              {renderBalanceSummary()}
            </Paper>
            <ExpenseList
              groupId={Number(group.id)}
              onSelect={(expense) => {
                // When an expense is selected for editing, set the editingExpense state and open the modal
                // Transform expense object to match what AddExpenseModal expects
                const transformedExpense = {
                  ...expense,
                  id: expense.id,
                  description: expense.title, // Map title to description for AddExpenseModal
                  paid_by: {
                    ...expense.paid_by,
                    id: String(expense.paid_by.id), // Ensure paid_by.id is a string
                  },
                  // Keep notes field if it exists
                  notes: (expense as any).notes || expense.description,
                  // Make sure recurring is a boolean
                  recurring: Boolean(expense.is_recurring || (expense as any).is_recurring),
                  // Ensure these fields exist with correct names
                  recurring_frequency: (expense as any).recurring_frequency || (expense as any).recurrence_pattern,
                  recurring_end_date: (expense as any).recurring_end_date || (expense as any).recurrence_end_date,
                };

                console.log('[GroupDetailView] Transforming expense for edit:', {
                  original: expense,
                  transformed: transformedExpense
                });

                setEditingExpense(transformedExpense);
                setShowAddExpense(true);
              }}
              accessToken={accessToken}
              refreshTrigger={expenseRefreshTrigger}
              onDataChange={() => {
                console.log('[GroupDetailView] ExpenseList data change detected, refreshing group data');
                refreshGroupData();
              }}
            />
          </Tabs.Panel>

          <Tabs.Panel value="categories">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Text size="lg" fw={600}>Group Categories</Text>
                <Text size="sm" c="dimmed">
                  {categories.length} {categories.length === 1 ? 'category' : 'categories'}
                </Text>
              </Group>

              {categoriesLoading ? (
                <div style={{ position: 'relative', minHeight: 200 }}>
                  <LoadingOverlay visible={true} />
                </div>
              ) : categories.length === 0 ? (
                <Paper p="xl" radius="md" style={{ textAlign: 'center' }}>
                  <Text c="dimmed" size="sm">
                    No categories found. Categories help organize your expenses.
                  </Text>
                  <Text c="dimmed" size="xs" mt="xs">
                    Create your first category when adding an expense!
                  </Text>
                </Paper>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                  {categories.map((category) => (
                    <Card
                      key={category.id}
                      p="md"
                      radius="md"
                      withBorder
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        // Optional: Open category details or edit modal
                        console.log('Category clicked:', category);
                      }}
                    >
                      <Group align="flex-start" gap="sm">
                        <ThemeIcon
                          size="lg"
                          radius="md"
                          variant="light"
                          color={category.icon ? 'blue' : 'gray'}
                        >
                          {category.icon || '📂'}
                        </ThemeIcon>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text fw={500} size="sm" truncate>
                            {category.name}
                          </Text>
                          {category.description && (
                            <Text size="xs" c="dimmed" mt={4} lineClamp={2}>
                              {category.description}
                            </Text>
                          )}
                        </div>
                      </Group>
                    </Card>
                  ))}
                </SimpleGrid>
              )}

              <Divider />

              <Paper p="md" radius="md" withBorder>
                <Text size="sm" fw={500} mb="xs">About Categories</Text>
                <Text size="xs" c="dimmed">
                  Categories help you organize your group expenses. When you create new expenses,
                  you can select from these categories or create new ones. Categories are shared
                  among all group members and help with expense tracking and reporting.
                </Text>
              </Paper>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="activity">
            {(() => {
              const member = group.members.find(m => m.user_id === user?.userId);
              console.log('[GroupDetailView] Rendering activity feed with:', {
                currentUserId: user?.userId,
                foundMember: !!member,
                lastViewedAt: member?.last_viewed_at || 'undefined',
                unreadCount: localUnreadCount
              });
              return null;
            })()}
            <GroupActivityFeed
              ref={activityFeedRef}
              groupId={groupId.toString()}
              lastViewedAt={
                // Find the current user's member in the group and get their last_viewed_at timestamp
                group.members.find(m => m.user_id === user?.userId)?.last_viewed_at || undefined
              }
              onActivitiesLoaded={(hasActs: boolean) => {
                console.log(`[GroupDetailView] Activities loaded, hasActivities: ${hasActs}, previous: ${hasActivities}`);

                // Only update our local state, don't trigger refreshes here
                setHasActivities(hasActs);

                // When activities are loaded in the activity tab, set unread count to 0
                if (activeTab === 'activity') {
                  console.log('[GroupDetailView] Setting local unread count to 0');
                  setLocalUnreadCount(0);
                }
              }}
              onSettlementDeleted={() => {
                console.log('[GroupDetailView] Settlement deleted, refreshing group data');
                refreshGroupData();
              }}
              forceRefreshTrigger={refreshTrigger}
            />
          </Tabs.Panel>

          <Tabs.Panel value="discussion">
            <GroupDiscussion
              groupId={groupId}
              userId={user?.userId || ''}
              accessToken={accessToken}
            />
          </Tabs.Panel>

          <Tabs.Panel value="members">
            {/* Prepare props specifically for MemberList */}
            {(() => {
              const memberListMembers: Member[] = membersList.map(m => ({
                ...m,
                id: String(m.id), // Convert id to string for Member type
                display_name: m.display_name || '', // Ensure display_name is string
                // Map 'removed' status to 'pending' for Member type compatibility
                status: m.status === 'removed' ? 'pending' : m.status as 'active' | 'pending' | 'ghost',
              }));
              // Use 'any' for Balance type temporarily and ensure string IDs
              const memberListBalances: any[] = (group.balances || []).map(b => ({
                ...b,
                from_member_id: String(b.from_member_id), // Convert IDs to string
                to_member_id: String(b.to_member_id),
                // Ensure other fields expected by MemberList are present if needed
              }));

              return (
                <MemberList
                  groupId={String(groupId)} // Keep as string
                  members={memberListMembers} // Pass string-ID members
                  balances={memberListBalances} // Pass string-ID balances
                  accessToken={accessToken}
                  onMemberChange={refresh}
                />
              );
            })()}
          </Tabs.Panel>
        </Card>
      </Tabs>

      <AddExpenseModal
        opened={showAddExpense}
        onClose={() => {
          setShowAddExpense(false);
          setEditingExpense(null);
        }}
        onSuccess={() => {
          setShowAddExpense(false);
          setEditingExpense(null);
          handleExpenseChange();
          // Refresh categories in case new ones were created
          refreshCategories();
        }}
        groupId={String(groupId)} // Pass groupId as string
        members={membersList} // Pass original GroupMember[]
        accessToken={accessToken}
        expense={editingExpense}
      />

      <SettleUpModal
        opened={showSettleUp}
        onClose={() => setShowSettleUp(false)}
        onSuccess={handleSettlementChange}
        groupId={Number(groupId)} // Keep groupId as number
        balances={group.balances || []} // Pass original number-ID balances
        accessToken={accessToken}
      />

      <InviteMemberModal
        opened={showInvite}
        onClose={() => setShowInvite(false)}
        onSuccess={refresh}
        groupId={Number(groupId)} // Pass groupId as number
        accessToken={accessToken}
        ghostCount={regularGhostCount}
        ghostLimit={getGhostLimit()}
      />

      {/* Leave Group Modal */}
      <Modal
        opened={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        title="Leave Group"
        centered
      >
        <Stack>
          {leaveGroupData.hasOutstandingBalances ? (
            <>
              <Text color="red" size="lg" fw={500}>You have outstanding balances</Text>
              <Text>
                You currently owe {leaveGroupData.formattedBalanceTotal} in this group. You need to settle all balances before you can leave the group.
              </Text>
              <Button
                mt="md"
                onClick={() => {
                  setShowLeaveModal(false);
                  setShowSettleUp(true);
                }}
              >
                Go to Settle Up
              </Button>
            </>
          ) : (
            <>
              <Text>
                Are you sure you want to leave the group "{group.name}"?
              </Text>
              <Text size="sm" c="dimmed">
                You won't be able to access the group's data after leaving.
                {group.owner_id === user?.userId && (
                  <Text component="span" fw={500}> As the owner, your ownership will be transferred to another member.</Text>
                )}
              </Text>
              <Group justify="flex-end" mt="md">
                <Button
                  variant="outline"
                  onClick={() => setShowLeaveModal(false)}
                  disabled={isLeaving}
                >
                  Cancel
                </Button>
                <Button
                  color="red"
                  onClick={async () => {
                    try {
                      setIsLeaving(true);
                      const result = await leaveGroup();
                      if (result.success) {
                        showSuccessNotification('You have left the group successfully');
                        setShowLeaveModal(false);

                        // Redirect to groups list
                        window.location.href = '/';
                      }
                    } catch (error) {
                      console.error('Error leaving group:', error);
                      showErrorNotification(error, 'Failed to leave group');
                    } finally {
                      setIsLeaving(false);
                    }
                  }}
                  loading={isLeaving}
                >
                  Leave Group
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </div>
  );
}; 